import type { Ref } from 'vue'
import { useStorage } from '@vueuse/core'

type UseCurrentServiceprovider = {
  uuid: string;
  name: string;
  freeSeats: number;
  hasLogo: boolean;
  dataLoaded: boolean;
  customEmail: {
    enabled: boolean;
  },
  exactBilling: boolean;
  newPricing: boolean;
  reseller: {
    isCreatedByReseller: boolean;
    id?: string;
    resellerInvoiced?: boolean;
  }
}

export const useCurrentServiceprovider = (): {
  serviceprovider: Ref<UseCurrentServiceprovider, UseCurrentServiceprovider>;
  getServiceproviderIdToSchema: () => string;
  setServiceprovider: (sp: UseCurrentServiceprovider) => void;
  initializeServiceprovider: (uuid: string) => Promise<void>;
} => {
  const { $api, $logger } = useNuxtApp()
  const serviceprovider: Ref<UseCurrentServiceprovider> = useStorage('serviceprovider', () => {
    return { uuid: '', freeSeats: 2, name: '', hasLogo: false, dataLoaded: false, customEmail: { enabled: false }, exactBilling: false, newPricing: false, reseller: { isCreatedByReseller: false } }
  }
  , sessionStorage, // invalidates on tab close
  { listenToStorageChanges: true })

  const initializeServiceprovider = async (uuid: string, forceReSync = false): Promise<void> => {
    // if serviceprovider does not have all required properties fetch it
    if (forceReSync || !isServiceproviderGood(uuid)) {
      const apiRes =
        await $api.serviceprovider.getServiceprovider(uuid)

      if (apiRes) {
        setServiceprovider({
          uuid,
          name: apiRes?.serviceproviderName ?? '',
          freeSeats: apiRes.license?.freeSeats ?? 2,
          hasLogo: apiRes?.whitelabel?.hasLogo ?? false,
          customEmail: apiRes?.customEmail,
          dataLoaded: true,
          exactBilling: apiRes?.exactBilling,
          newPricing: apiRes?.newPricing ?? false,
          reseller: apiRes?.reseller
        })
      } else {
        $logger.error(new Error(`Serviceprovider not found: ${uuid}`))
      }
    } else {
      $logger.info('Serviceprovider already loaded')
    }
  }

  // check if sp properties are good, otherwise fetch
  function isServiceproviderGood (newSpUUID: string): boolean {
    if (!serviceprovider.value) {
      return false
    }

    // required for admin when switching between serviceproviders
    if (serviceprovider.value.uuid !== newSpUUID) {
      return false
    }

    if (!Object.hasOwn(serviceprovider.value, 'uuid') || !Object.hasOwn(serviceprovider.value, 'dataLoaded')) {
      return false
    }

    if (serviceprovider.value.name.length === 0) {
      return false
    }

    if (!Object.hasOwn(serviceprovider.value, 'customEmail')) {
      return false
    }

    if (!Object.hasOwn(serviceprovider.value, 'reseller')) {
      return false
    }

    return true
  }

  const setServiceprovider = (sp: UseCurrentServiceprovider): void => {
    // if already set, use the existing name, otherwise overwrite
    serviceprovider.value = {
      ...serviceprovider.value,
      ...sp,
      dataLoaded: true
    }
  }

  const getServiceproviderIdToSchema = (): string => {
    if (serviceprovider.value.uuid) {
      return serviceprovider.value.uuid.replace(/-/g, '_')
    } else {
      return ''
    }
  }

  return {
    serviceprovider,
    setServiceprovider,
    initializeServiceprovider,
    getServiceproviderIdToSchema
  }
}
